import type { EditionMeta, EditionRecord, PersonalEdition } from "./types";

export type User = {
  id: string;
  email: string;
  name: string;
  role: "admin" | "editor";
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

export const api = {
  me: () => request<{ user: User }>("/api/auth/me"),
  login: (email: string, password: string) =>
    request<{ user: User }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  listEditions: (family?: string) =>
    request<{ editions: EditionMeta[] }>(
      `/api/editions${family ? `?family=${family}` : ""}`
    ),
  createEdition: (family: string, opts?: { cloneFromId?: string; name?: string }) =>
    request<{ edition: EditionRecord }>("/api/editions", {
      method: "POST",
      body: JSON.stringify({ family, ...opts }),
    }),
  getEdition: (id: string) =>
    request<{ edition: EditionRecord }>(`/api/editions/${id}`),
  updateEdition: (id: string, data: PersonalEdition) =>
    request<{ edition: EditionRecord }>(`/api/editions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ data }),
    }),
  publishEdition: (id: string) =>
    request<{ edition: EditionRecord }>(`/api/editions/${id}/publish`, {
      method: "POST",
    }),
  deleteEdition: (id: string) =>
    request<{ ok: true }>(`/api/editions/${id}`, { method: "DELETE" }),
};
